import { useState, type ReactNode } from "react";
import { SYSTEM_LOCALITY, SYSTEM_LOGO_PATH, SYSTEM_SHORT_NAME } from "../app/branding";
import { Alert, Avatar, Breadcrumb, Button, Layout, Menu, Result, Skeleton, Tag, Typography, type MenuProps } from "antd";
import ApartmentOutlined from "@ant-design/icons/ApartmentOutlined";
import EnvironmentOutlined from "@ant-design/icons/EnvironmentOutlined";
import MenuFoldOutlined from "@ant-design/icons/MenuFoldOutlined";
import MenuUnfoldOutlined from "@ant-design/icons/MenuUnfoldOutlined";
import ReadOutlined from "@ant-design/icons/ReadOutlined";
import SafetyCertificateOutlined from "@ant-design/icons/SafetyCertificateOutlined";
import SettingOutlined from "@ant-design/icons/SettingOutlined";
import SolutionOutlined from "@ant-design/icons/SolutionOutlined";
import UserOutlined from "@ant-design/icons/UserOutlined";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useActorContext, useActorContextActions } from "../auth/ActorContext";
import { useAuthenticationActions } from "../auth/AuthContext";
import {
  findNavigationItem,
  isNavigationItemVisible,
  navigationGroups,
  navigationItems,
  type NavigationItem,
} from "../navigation/navigation";

const NAVIGATION_ICONS: Readonly<Record<string, ReactNode>> = {
  "martyr-list": <SolutionOutlined aria-hidden="true" />,
  cemeteries: <EnvironmentOutlined aria-hidden="true" />,
  "memorial-entries": <ReadOutlined aria-hidden="true" />,
  "administration-area": <ApartmentOutlined aria-hidden="true" />,
  "user-accounts": <UserOutlined aria-hidden="true" />,
  "role-assignments": <SafetyCertificateOutlined aria-hidden="true" />,
};

const GROUP_ICONS: Readonly<Record<string, ReactNode>> = {
  "grave-cemetery": <EnvironmentOutlined aria-hidden="true" />,
  administration: <SettingOutlined aria-hidden="true" />,
};

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length === 0 || !words[0]) return "CB";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  const first = words[0][0] ?? "";
  const last = words[words.length - 1]?.[0] ?? "";
  return (first + last).toUpperCase();
}

export function AppShell({ children }: { readonly children: ReactNode }) {
  const state = useActorContext();
  const { revalidate } = useActorContextActions();
  const location = useLocation();
  const { logout } = useAuthenticationActions();
  const [collapsed, setCollapsed] = useState(false);

  const actor =
    state.status === "ready" || state.status === "refreshing" || state.status === "stale"
      ? state.actor
      : null;
  const roleCodes = actor?.grants.map((grant) => grant.roleCode) ?? [];
  const selectedItem = findNavigationItem(location.pathname);
  const selectedKeys = selectedItem ? [selectedItem.route] : [];

  // Nhóm còn từ hai mục hiển thị trở lên thì giữ dạng SubMenu phân cấp. Nhóm chỉ còn đúng một mục
  // hiển thị thì chính mục đó lên cấp cao nhất: không bọc SubMenu, không sinh crumb nhóm riêng.
  const visibleGroups = navigationGroups
    .map((group) => ({
      group,
      items: group.items.filter((item) => isNavigationItemVisible(item, roleCodes, actor)),
    }))
    .filter((entry) => entry.items.length > 0);

  const openableKeys = visibleGroups
    .filter((entry) => entry.items.length > 1)
    .map((entry) => entry.group.id);

  // Mặc định luôn mở tất cả SubMenu có thể hiển thị; cho phép user tự do đóng/mở
  const [openKeys, setOpenKeys] = useState<string[]>(openableKeys);

  // `end={true}` khi có mục khác là con của route này: giữ cho mục cha không bị tô sáng kép với mục
  // con trên cùng một route. Mục không có mục con (ví dụ `/martyrs`) vẫn active ở route lồng.
  const renderItemLabel = (item: NavigationItem) => (
    <NavLink
      to={item.route}
      end={item.route === "/" || navigationItems.some((other) => other.route.startsWith(`${item.route}/`))}
    >
      <span>{item.label}</span>
    </NavLink>
  );

  const menuItems: NonNullable<MenuProps["items"]> = visibleGroups.map(({ group, items }) => {
    const groupIcon = GROUP_ICONS[group.id];

    if (items.length === 1) {
      const item = items[0]!;
      return {
        key: item.route,
        icon: NAVIGATION_ICONS[item.id] ?? groupIcon,
        label: renderItemLabel(item),
      };
    }

    return {
      key: group.id,
      icon: groupIcon,
      label: group.label,
      children: items.map((item) => ({
        key: item.route,
        icon: NAVIGATION_ICONS[item.id] ?? groupIcon,
        label: renderItemLabel(item),
      })),
    };
  });

  const handleOpenChange = (keys: string[]) => {
    // Khi sidebar đang mở bình thường, cập nhật trạng thái mở của người dùng
    if (!collapsed) {
      setOpenKeys(keys);
    }
  };

  const handleToggleCollapse = () => {
    setCollapsed((prev) => !prev);
  };

  // Breadcrumb: gốc luôn là Hồ sơ liệt sĩ. Crumb nhóm chỉ xuất hiện khi nhóm còn từ hai mục hiển
  // thị — nhóm một mục đã là mục cấp cao nhất nên không lặp lại tên nhóm. Nhóm chưa có trang riêng
  // nên crumb nhóm trỏ tới mục con ĐẦU TIÊN đang hiển thị theo quyền.
  const currentGroup = navigationGroups.find((group) =>
    group.items.some((item) => selectedItem && item.route === selectedItem.route),
  );
  const currentGroupVisibleItems = currentGroup
    ? currentGroup.items.filter((item) => isNavigationItemVisible(item, roleCodes, actor))
    : [];
  const showGroupCrumb = currentGroup !== undefined && currentGroupVisibleItems.length !== 1;

  const breadcrumbItems = [
    {
      title: <Link to="/martyrs">Hệ thống tác nghiệp</Link>,
    },
    ...(showGroupCrumb && currentGroup
      ? [
          {
            title: (
              <Link to={currentGroupVisibleItems[0]?.route ?? location.pathname}>
                {currentGroup.label}
              </Link>
            ),
          },
        ]
      : []),
    ...(selectedItem
      ? [
          {
            title: (
              <Link
                to={selectedItem.route}
                aria-current={location.pathname === selectedItem.route ? "page" : undefined}
              >
                {selectedItem.label}
              </Link>
            ),
          },
        ]
      : []),
  ];

  const isFullBleed = location.pathname.includes("/gis");

  return (
    <Layout className="app-shell">
      <Layout.Sider
        className={`sidebar ${collapsed ? "sidebar-collapsed" : ""}`}
        breakpoint="lg"
        collapsedWidth={64}
        collapsed={collapsed}
        onCollapse={setCollapsed}
        width="var(--ncc-sidebar-width)"
      >
        <div className="brand">
          <Avatar
            className="brand-mark"
            shape="circle"
            aria-hidden="true"
            src={SYSTEM_LOGO_PATH}
            alt=""
          >
            QN
          </Avatar>
          <div className="brand-copy" aria-hidden={collapsed}>
            <Typography.Text className="brand-title">{SYSTEM_SHORT_NAME}</Typography.Text>
            <Typography.Text className="brand-subtitle">{SYSTEM_LOCALITY}</Typography.Text>
          </div>
        </div>
        <div className="brand-divider" aria-hidden="true" />
        <nav aria-label="Điều hướng chính">
          <Menu
            items={menuItems}
            mode="inline"
            selectedKeys={selectedKeys}
            openKeys={collapsed ? [] : openKeys}
            onOpenChange={handleOpenChange}
            theme="dark"
          />
        </nav>
      </Layout.Sider>
      <Layout className="app-column">
        <Layout.Header className="app-header">
          <div className="header-left">
            <Button
              type="text"
              className="sidebar-toggle-btn"
              aria-label={collapsed ? "Mở rộng menu" : "Thu gọn menu"}
              aria-expanded={!collapsed}
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={handleToggleCollapse}
            />
            <Breadcrumb items={breadcrumbItems} className="header-breadcrumb" />
          </div>

          <div className="header-actions">
            {state.status === "loading" ? (
              <div className="actor-context" aria-label="Đang tải ngữ cảnh người dùng" role="status">
                <Skeleton.Input active size="small" />
              </div>
            ) : actor ? (
              <div className="actor-context" aria-label="Ngữ cảnh người dùng hiện tại">
                <Avatar className="actor-avatar" shape="circle">
                  {getInitials(actor.displayName)}
                </Avatar>
                <div className="actor-info">
                  <Typography.Text strong className="actor-name">{actor.displayName}</Typography.Text>
                  <Tag className="actor-org-tag" bordered={false}>{actor.homeOrganization.name}</Tag>
                </div>
              </div>
            ) : null}
            <Button className="logout-button" onClick={() => void logout()}>
              Đăng xuất
            </Button>
          </div>
        </Layout.Header>
        <Layout.Content>
          <main id="main-content" className={isFullBleed ? "main-content main-content--full" : "main-content"}>
            {state.status === "forbidden" ? (
              <Result
                status="warning"
                title="Tài khoản không khả dụng"
                subTitle="Tài khoản chưa được cấp quyền sử dụng hệ thống."
                extra={<Button type="primary" onClick={() => void logout()}>Đăng xuất</Button>}
              />
            ) : state.status === "error" ? (
              <Result
                status="error"
                title="Không thể tải ngữ cảnh người dùng"
                subTitle={state.requestId ? `Mã hỗ trợ: ${state.requestId}` : "Vui lòng thử lại sau."}
                extra={<Button type="primary" onClick={revalidate}>Thử lại</Button>}
              />
            ) : state.status === "stale" ? (
              <div className="stale-wrap">
                <Alert
                  type="warning"
                  showIcon
                  title="Thông tin ngữ cảnh cần được làm mới"
                  description={state.requestId ? `Mã hỗ trợ: ${state.requestId}` : undefined}
                  action={<Button size="small" onClick={revalidate}>Thử lại</Button>}
                />
                {children}
              </div>
            ) : state.status === "loading" ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
              children
            )}
          </main>
        </Layout.Content>
      </Layout>
    </Layout>
  );
}
